import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { findClosestParent } from '~/Partials/Utils/utils';
import { MouseButton } from '~/Utils/MouseButton';

const isNonEmpty = (selection: Selection | null): selection is Selection => !isEmpty(selection?.toString());

const isLabel = (element: Element): element is HTMLLabelElement => element.tagName.toLowerCase() === 'label';

const willBeHandledByAssociatedElement = (eventTarget: Element, clickableElement: HTMLElement) => {
    const nearestLabel = findClosestParent(eventTarget, isLabel);

    if (nearestLabel === null) {
        return false;
    }

    const associatedElementId = nearestLabel.getAttribute('for');

    if (!associatedElementId) {
        return false;
    }

    return associatedElementId === clickableElement.id;
};

const portalContainerId = 'portal-container';

// Due to event propagation from portal to React tree we have to manually exclude these events.
// https://github.com/facebook/react/issues/11387
const willBubbleOutsideOfPortal = (eventTarget: Node | null, clickableElement: HTMLElement) => {
    let potentialPortalContainer = eventTarget;
    let previouslyCheckedElement: Node | null = null;

    while (potentialPortalContainer) {
        if (potentialPortalContainer instanceof HTMLElement && potentialPortalContainer.id === portalContainerId) {
            // If we find a container, we are sure that the previous element is a root element for one of the portals.
            // Then we only check whether the clickable element is part of that particular portal's tree so that
            // multiple portals do not interfere with each other.
            // If previouslyCheckedElement is null it means that event target is portal container itself.
            // Then we treat an event as if it would bubble outside the portal.
            return !previouslyCheckedElement || !previouslyCheckedElement.contains(clickableElement);
        }

        previouslyCheckedElement = potentialPortalContainer;
        potentialPortalContainer = potentialPortalContainer.parentElement;
    }

    return false;
};

const shouldIgnoreEvent = (eventTarget: EventTarget, clickableElement: HTMLElement | null) =>
    clickableElement === null ||
    !(eventTarget instanceof Element) ||
    clickableElement.contains(eventTarget) ||
    willBeHandledByAssociatedElement(eventTarget, clickableElement) ||
    willBubbleOutsideOfPortal(eventTarget, clickableElement);

export const useClickProxyAllowingTextSelection = <TClickableElement extends HTMLElement>() => {
    const clickableRef = React.useRef<TClickableElement>(null);

    const onProxyMouseDown = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (shouldIgnoreEvent(event.target, clickableRef.current)) {
            return;
        }

        if (event.button === MouseButton.Left) {
            const userSelection = getSelection();

            if (isNonEmpty(userSelection)) {
                userSelection.removeAllRanges();
            }
        }

        if (event.button === MouseButton.Middle) {
            event.preventDefault();
        }
    };

    const onProxyMouseUp = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (shouldIgnoreEvent(event.target, clickableRef.current)) {
            return;
        }

        if (event.button === MouseButton.Left) {
            if (isNonEmpty(getSelection())) {
                return;
            }

            clickableRef.current?.click();
            clickableRef.current?.focus();
        }

        if (event.button === MouseButton.Middle) {
            const eventCopy = new MouseEvent('click', { button: event.button, bubbles: true });

            clickableRef.current?.dispatchEvent(eventCopy);
        }
    };

    return { clickableRef, onProxyMouseDown, onProxyMouseUp };
};
